import { FormDocument } from '@/features/ui/DocumentsData/model/types';

import {
  AddressPayload,
  AttachmentPayload,
  buildOwner,
  generateApplicationAttachment,
  mapAddress,
  OwnerPayload,
  toStringValue,
} from '@/shared/lib/helpers/payloadHelpers';

import { ServiceFileManager } from '@/helpers/serviceFileManager';

import {
  DocumentTypeCode,
  FormDataType,
  PurchaseItemIdMapping,
  UserRequestDraft,
  UserService,
  Weapon,
} from '@/api/types';

import { collectAttachments } from './payloadHelpers';

type GeneratePayloadParams = {
  requestId: UserRequestDraft['id'];
  formData: FormDataType;
  service: UserService;
  serviceFileManager?: ServiceFileManager;
};

interface Payload {
  requestId: UserRequestDraft['id'];
  service: {
    id: string;
    name: string;
    code: string;
  };
  purchase: {
    object_id: string;
    object_name: string;
    weapon: Nullable<{
      id: string;
      type: string;
    }>;
  };
  owner: OwnerPayload;
  is_addresses_match: boolean;
  registered_address: AddressPayload;
  declared_address: AddressPayload;
  org_npu: {
    id: Nullable<string>;
    name: Nullable<string>;
    isPayable?: boolean;
  };
  personal_status?: {
    id: Nullable<string>;
    name: Nullable<string>;
  };
  additional_documents: {
    additional_info?: Nullable<string>;
  };
  attachments: AttachmentPayload[];
}

export const generatePayload = async ({
  requestId,
  formData,
  service,
  serviceFileManager,
}: GeneratePayloadParams): Promise<{
  commonPayload: Payload;
  signedPayload: Payload & Partial<ReturnType<ServiceFileManager['getSignature']>>;
}> => {
  const passportDocument = (
    Array.isArray(formData.personPassportDocument) ? formData.personPassportDocument[0] : null
  ) as Nullable<FormDocument>;
  const hasActualAddress = (formData.isAddressesEqual as { id?: string })?.id === '0';
  const attachments = collectAttachments(formData, hasActualAddress);
  const applicationAttachment = await generateApplicationAttachment(DocumentTypeCode.PURCHASE, serviceFileManager);

  const commonPayload: Payload = {
    requestId,
    service: {
      id: service.id,
      name: service.name,
      code: service.code,
    },
    purchase: {
      object_id: toStringValue(((formData.additionalObjectOfPurchase ?? formData.objectOfPurchase) as any)?.id) ?? '',
      object_name:
        toStringValue(
          (
            ((formData.additionalObjectOfPurchase as any)?.id === PurchaseItemIdMapping.WEAPONS_MAIN_PART
              ? formData.additionalObjectOfPurchase
              : formData.objectOfPurchase) as any
          )?.label,
        ) ?? '',
      weapon: formData.weapon
        ? {
            id: (formData.weapon as Weapon)?.id,
            type: (formData.weapon as Weapon)?.weaponType,
          }
        : null,
    },
    personal_status: {
      id: toStringValue((formData.personStatus as Record<string, unknown>)?.id),
      name: toStringValue((formData.personStatus as Record<string, unknown>)?.label),
    },
    owner: buildOwner(formData, passportDocument),
    is_addresses_match: !hasActualAddress,
    registered_address: mapAddress(formData.registeredAddress as Record<string, any>),
    declared_address: mapAddress(
      (hasActualAddress ? formData.actualAddress : formData.registeredAddress) as Record<string, any>,
    ),
    org_npu: {
      id: toStringValue((formData.orgNpu as Record<string, unknown>)?.id),
      name: toStringValue((formData.orgNpu as Record<string, unknown>)?.label),
    },
    additional_documents: {
      additional_info: toStringValue(formData.additionalInformation),
    },
    attachments,
  };

  const signedPayload = {
    ...commonPayload,
    attachments: applicationAttachment ? [...attachments, applicationAttachment] : attachments,
    ...(serviceFileManager?.getSignature() ?? {}),
  };

  return {
    commonPayload,
    signedPayload,
  };
};
